/**
 * @zendir/ui - LogViewer Component
 *
 * Real-time log/event viewer for mission operations and system monitoring.
 * Supports severity filtering, search, auto-scroll, pause/resume,
 * and timestamped entries with color-coded severity levels.
 *
 * Features:
 * - Severity levels: debug, info, warn, error, critical
 * - Color-coded entries with Astro status colors
 * - Real-time auto-scroll with pause/resume
 * - Search/filter by text
 * - Severity filter checkboxes
 * - Timestamp display
 * - Source/component tags
 * - Export log to clipboard or file
 * - Maximum log retention (auto-prune old entries)
 * - Virtualized rendering for large logs
 *
 * @example
 * ```tsx
 * <LogViewer
 *   entries={logEntries}
 *   maxEntries={5000}
 *   showTimestamp
 *   showSource
 * />
 * ```
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  memo,
} from "react";
import { useTheme } from "../../theme";

// =============================================================================
// Types
// =============================================================================

export type LogSeverity = "debug" | "info" | "warn" | "error" | "critical";

export interface LogEntry {
  /** Unique ID */
  id: string | number;
  /** Wall-clock timestamp (ISO string or Date) for ordering/export. */
  timestamp: string | Date;
  /**
   * Simulation-elapsed time in seconds (T+ format) when the event occurred.
   * Optional. When provided AND `timestampFormat="sim-elapsed"`, this is what
   * the timestamp column displays. Falls through to `timestamp` otherwise.
   */
  simTime?: number;
  /** Severity level */
  severity: LogSeverity;
  /** Log message */
  message: string;
  /** Source component/system */
  source?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

const DEFAULT_ESTIMATED_ROW_HEIGHT = 28;
const DEFAULT_OVERSCAN_COUNT = 12;

export interface LogViewerProps {
  /** Array of log entries */
  entries: LogEntry[];
  /** Maximum entries to keep in memory (default: 5000). Older entries are pruned. */
  maxEntries?: number;
  /** Height of the viewer (default: 400) */
  height?: number | string;
  /** Title */
  title?: string;
  /** Show timestamps (default: true) */
  showTimestamp?: boolean;
  /** Show source column (default: true) */
  showSource?: boolean;
  /** Show severity filter toolbar (default: true) */
  showFilters?: boolean;
  /** Show search bar (default: true) */
  showSearch?: boolean;
  /** Initial severity filters (all enabled by default) */
  defaultSeverities?: LogSeverity[];
  /**
   * Timestamp format. `'sim-elapsed'` displays `entry.simTime` as `T+HHH:MM:SS.s`
   * (falls back to wall-clock formatting if simTime is absent on an entry).
   * Default: 'time'.
   */
  timestampFormat?: "time" | "datetime" | "relative" | "sim-elapsed";
  /** Called when an entry is clicked */
  onEntryClick?: (entry: LogEntry) => void;
  /** CSS class */
  className?: string;
  /** Show outer container border (default: true). Set false when embedded inside a Container/card. */
  bordered?: boolean;
  /** Custom style overrides for the outer container */
  style?: React.CSSProperties;
  /** Estimated row height in px for virtualization (default: 28). Enables smooth scroll through large logs. */
  estimatedRowHeight?: number;
  /** Number of rows to render above/below viewport for virtualization (default: 12). */
  overscanCount?: number;
  /**
   * Enable pagination: show entries in pages with Previous/Next.
   * Page 1 = newest entries. Use with large maxEntries so users can navigate to older pages.
   * When set, scrolling is replaced by page navigation (better for very large logs).
   */
  pagination?: boolean | { pageSize?: number };
}

// =============================================================================
// Helpers
// =============================================================================

const SEVERITY_ORDER: LogSeverity[] = [
  "debug",
  "info",
  "warn",
  "error",
  "critical",
];

function formatSimElapsed(seconds: number): string {
  const sign = seconds < 0 ? "-" : "+";
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const whole = Math.floor(s);
  const tenths = Math.floor((s - whole) * 10);
  return `T${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(whole).padStart(2, "0")}.${tenths}`;
}

function formatTimestamp(
  entry: Pick<LogEntry, "timestamp" | "simTime">,
  format: "time" | "datetime" | "relative" | "sim-elapsed",
): string {
  if (
    format === "sim-elapsed" &&
    typeof entry.simTime === "number" &&
    Number.isFinite(entry.simTime)
  ) {
    return formatSimElapsed(entry.simTime);
  }

  const ts = entry.timestamp;
  const d = typeof ts === "string" ? new Date(ts) : ts;
  if (isNaN(d.getTime())) return String(ts);

  if (format === "sim-elapsed") {
    // Fallback when simTime missing — show wall-clock time so the row isn't blank.
    return d.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  if (format === "time") {
    return (
      d.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }) +
      "." +
      d.getMilliseconds().toString().padStart(3, "0")
    );
  }
  if (format === "datetime") {
    return (
      d.toLocaleDateString("en-US", { month: "short", day: "2-digit" }) +
      " " +
      d.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
  }
  // relative
  const diff = Date.now() - d.getTime();
  if (diff < 1000) return "just now";
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

// =============================================================================
// Component
// =============================================================================

export const LogViewer = memo(function LogViewer({
  entries,
  maxEntries = 5000,
  height = 400,
  title,
  showTimestamp = true,
  showSource = true,
  showFilters = true,
  showSearch = true,
  defaultSeverities,
  timestampFormat = "time",
  onEntryClick,
  className = "",
  bordered = true,
  style: styleProp,
  estimatedRowHeight = DEFAULT_ESTIMATED_ROW_HEIGHT,
  overscanCount = DEFAULT_OVERSCAN_COUNT,
  pagination,
}: LogViewerProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState<Set<LogSeverity>>(
    new Set(defaultSeverities ?? SEVERITY_ORDER),
  );
  const [autoScroll, setAutoScroll] = useState(true);
  const prevLengthRef = useRef(entries.length);
  /** Scroll position and container height for virtualization */
  const [scrollState, setScrollState] = useState({
    scrollTop: 0,
    containerHeight: 300,
  });
  const rafRef = useRef<number | null>(null);

  const severityColors: Record<LogSeverity, string> = {
    debug: tokens.colors.text.muted ?? tokens.colors.text.secondary,
    info: tokens.colors.status.standby,
    warn: tokens.colors.status.caution,
    error: tokens.colors.status.serious,
    critical: tokens.colors.status.critical,
  };

  /**
   * Astro UX status shapes mapped to log severity levels:
   *   debug → off (small dot ·), info → standby (ring ◯),
   *   warn → caution (square ■), error → serious (diamond ◆),
   *   critical → critical (triangle down ▼)
   */
  const severityShapes: Record<LogSeverity, string> = {
    debug: "·",
    info: "○",
    warn: "■",
    error: "◆",
    critical: "▼",
  };

  const severityLabels: Record<LogSeverity, string> = {
    debug: "DBG",
    info: "INF",
    warn: "WRN",
    error: "ERR",
    critical: "CRT",
  };

  // Filter & limit entries: keep NEWEST maxEntries (entries[0] = newest)
  const filteredEntries = useMemo(() => {
    let filtered = entries.filter((e) => severityFilter.has(e.severity));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.message.toLowerCase().includes(q) ||
          (e.source && e.source.toLowerCase().includes(q)),
      );
    }
    return filtered.slice(0, maxEntries);
  }, [entries, severityFilter, searchQuery, maxEntries]);

  const paginationConfig =
    pagination === true ? { pageSize: 200 } : pagination || undefined;
  const pageSize = paginationConfig?.pageSize ?? 200;
  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const [currentPage, setCurrentPage] = useState(1);

  // Clamp current page when totalPages shrinks (e.g. after filter change)
  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const paginationEntries = useMemo(() => {
    if (!paginationConfig) return null;
    const start = (currentPage - 1) * pageSize;
    return filteredEntries.slice(start, start + pageSize);
  }, [paginationConfig, currentPage, pageSize, filteredEntries]);

  const totalHeight = filteredEntries.length * estimatedRowHeight;
  const { scrollTop, containerHeight } = scrollState;
  const visibleRange = useMemo(() => {
    const start = Math.max(
      0,
      Math.floor(scrollTop / estimatedRowHeight) - overscanCount,
    );
    const end = Math.min(
      filteredEntries.length,
      Math.ceil((scrollTop + containerHeight) / estimatedRowHeight) +
        overscanCount,
    );
    return { start, end };
  }, [
    scrollTop,
    containerHeight,
    estimatedRowHeight,
    overscanCount,
    filteredEntries.length,
  ]);
  const visibleEntries = useMemo(
    () => filteredEntries.slice(visibleRange.start, visibleRange.end),
    [filteredEntries, visibleRange.start, visibleRange.end],
  );

  const usePagination = !!paginationConfig;

  const renderRow = useCallback(
    (entry: LogEntry) => (
      <div
        key={entry.id}
        role={onEntryClick ? "button" : "listitem"}
        tabIndex={onEntryClick ? 0 : undefined}
        aria-label={
          onEntryClick
            ? `${severityLabels[entry.severity]}: ${entry.message}`
            : undefined
        }
        onClick={() => onEntryClick?.(entry)}
        onKeyDown={
          onEntryClick
            ? (e) =>
                (e.key === "Enter" || e.key === " ") &&
                (e.preventDefault(), onEntryClick(entry))
            : undefined
        }
        style={{
          display: "flex",
          alignItems: "flex-start",
          minHeight: estimatedRowHeight,
          boxSizing: "border-box",
          padding: "3px 12px",
          borderBottom: `1px solid ${tokens.colors.border.muted}11`,
          cursor: onEntryClick ? "pointer" : "default",
          transition: "background 150ms ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.background =
            `${tokens.colors.background.surface}`;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.background = "transparent";
        }}
      >
        <span
          style={{
            color: severityColors[entry.severity],
            fontWeight: 700,
            fontSize: tokens.typography.fontSize.xxs,
            minWidth: 38,
            textAlign: "center",
            background: `${severityColors[entry.severity]}18`,
            borderRadius: tokens.borderRadius.sm,
            padding: "1px 4px",
            marginRight: 8,
            marginTop: 1,
            letterSpacing: "0.3px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <span style={{ fontSize: "8px", lineHeight: 1 }}>
            {severityShapes[entry.severity]}
          </span>
          {severityLabels[entry.severity]}
        </span>
        {showTimestamp && (
          <span
            style={{
              color: tokens.colors.text.muted,
              marginRight: 8,
              // 'sim-elapsed' renders T+HH:MM:SS.s which is wider than the wall-clock formats.
              minWidth: timestampFormat === "sim-elapsed" ? 110 : 85,
              fontSize: 11,
              flexShrink: 0,
              fontFamily: tokens.typography.fontFamily.mono,
            }}
          >
            {formatTimestamp(entry, timestampFormat)}
          </span>
        )}
        {showSource && entry.source && (
          <span
            style={{
              color: tokens.colors.interactive.default,
              marginRight: 8,
              fontSize: 11,
              minWidth: 70,
              maxWidth: 100,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            [{entry.source}]
          </span>
        )}
        <span
          style={{
            color:
              entry.severity === "error" || entry.severity === "critical"
                ? severityColors[entry.severity]
                : tokens.colors.text.primary,
            flex: 1,
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {entry.message}
        </span>
      </div>
    ),
    [
      severityColors,
      severityShapes,
      severityLabels,
      estimatedRowHeight,
      tokens,
      showTimestamp,
      showSource,
      timestampFormat,
      onEntryClick,
    ],
  );

  // Measure scroll container height
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setScrollState((s) => ({ ...s, containerHeight: el.clientHeight }));
    });
    ro.observe(el);
    setScrollState((s) => ({ ...s, containerHeight: el.clientHeight }));
    return () => ro.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop: st, scrollHeight, clientHeight } = el;
    const atBottom = scrollHeight - st - clientHeight < 30;
    setAutoScroll(atBottom);
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setScrollState((prev) => ({ ...prev, scrollTop: el.scrollTop }));
    });
  }, []);

  // Auto-scroll to bottom when new entries arrive and user is at bottom (scroll mode).
  // In pagination mode: page 1 always shows newest entries (entries are prepended) so
  // content updates automatically — no forced jump needed. The user decides when to
  // follow new entries by clicking "First" (which sets autoScroll=true).
  useEffect(() => {
    if (entries.length <= prevLengthRef.current) {
      prevLengthRef.current = entries.length;
      return;
    }
    if (!usePagination && !paused && autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevLengthRef.current = entries.length;
  }, [entries.length, paused, autoScroll, usePagination]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const toggleSeverity = useCallback((sev: LogSeverity) => {
    setSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });
  }, []);

  const exportLog = useCallback(() => {
    const text = filteredEntries
      .map((e) => {
        const wall = formatTimestamp(e, "datetime");
        // When sim-elapsed is in use, include the sim T+ stamp alongside wall time so
        // exports remain useful when correlating with sim playback.
        const sim =
          timestampFormat === "sim-elapsed" && typeof e.simTime === "number"
            ? ` ${formatSimElapsed(e.simTime)}`
            : "";
        const src = e.source ? `[${e.source}]` : "";
        return `${wall}${sim} [${e.severity.toUpperCase()}] ${src} ${e.message}`;
      })
      .join("\n");
    navigator.clipboard?.writeText(text);
  }, [filteredEntries, timestampFormat]);

  const clearFilters = useCallback(() => {
    setSeverityFilter(new Set(SEVERITY_ORDER));
    setSearchQuery("");
  }, []);

  // Counts by severity
  const counts = useMemo(() => {
    const c: Record<LogSeverity, number> = {
      debug: 0,
      info: 0,
      warn: 0,
      error: 0,
      critical: 0,
    };
    entries.forEach((e) => {
      c[e.severity]++;
    });
    return c;
  }, [entries]);

  return (
    <div
      className={`log-viewer ${className}`}
      style={{
        height: typeof height === "number" ? `${height}px` : height,
        minHeight: typeof height === "number" ? `${height}px` : height,
        background: tokens.colors.background.base,
        ...(bordered
          ? (tokens.colors.border.cardStyle ?? {
              border: `1px solid ${tokens.colors.border.muted}`,
            })
          : { border: "none" }),
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: tokens.typography.fontFamily.mono,
        ...styleProp,
        fontSize: tokens.typography.fontSize.xs,
        color: tokens.colors.text.primary,
        backdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
      }}
    >
      {/* Header */}
      {title && (
        <div
          style={{
            padding: "8px 12px",
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: tokens.colors.background.surface,
            fontFamily: tokens.typography.fontFamily.primary,
            backdropFilter: "blur(8px)",
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>{title}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: tokens.colors.text.muted }}>
              {filteredEntries.length.toLocaleString()} entries
            </span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {(showFilters || showSearch) && (
        <div
          style={{
            padding: "6px 12px",
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            background: tokens.colors.background.surface,
            fontFamily: tokens.typography.fontFamily.primary,
          }}
        >
          {showFilters &&
            SEVERITY_ORDER.map((sev) => (
              <button
                key={sev}
                onClick={() => toggleSeverity(sev)}
                aria-pressed={severityFilter.has(sev)}
                aria-label={`Filter ${sev} logs (${counts[sev]})`}
                style={{
                  background: severityFilter.has(sev)
                    ? `${severityColors[sev]}22`
                    : "transparent",
                  color: severityFilter.has(sev)
                    ? severityColors[sev]
                    : tokens.colors.text.muted,
                  border: `1px solid ${severityFilter.has(sev) ? severityColors[sev] + "66" : tokens.colors.border.muted}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
                  fontSize: tokens.typography.fontSize.xxs,
                  fontWeight: tokens.typography.fontWeight.medium,
                  cursor: "pointer",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  transition: "all 150ms ease",
                }}
              >
                <span aria-hidden="true">{severityShapes[sev]}</span> {sev} (
                {counts[sev]})
              </button>
            ))}
          <div style={{ flex: 1 }} />
          {showSearch && (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter messages..."
              aria-label="Filter log messages"
              style={{
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                padding: `3px ${tokens.spacing.sm}`,
                fontSize: tokens.typography.fontSize.sm,
                outline: "none",
                minWidth: 150,
              }}
            />
          )}
          <button
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? "Resume log stream" : "Pause log stream"}
            style={{
              background: paused
                ? `${tokens.colors.status.caution}22`
                : tokens.colors.background.base,
              color: paused
                ? tokens.colors.status.caution
                : tokens.colors.text.secondary,
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
              fontSize: tokens.typography.fontSize.xxs,
              cursor: "pointer",
              fontWeight: tokens.typography.fontWeight.medium,
              transition: "all 150ms ease",
            }}
          >
            {paused ? "\u25B6 Resume" : "\u23F8 Pause"}
          </button>
          <button
            onClick={exportLog}
            aria-label="Copy log to clipboard"
            style={{
              background: tokens.colors.background.base,
              color: tokens.colors.text.secondary,
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
              fontSize: tokens.typography.fontSize.xxs,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            Copy
          </button>
          <button
            onClick={clearFilters}
            aria-label="Reset all filters"
            style={{
              background: tokens.colors.background.base,
              color: tokens.colors.text.secondary,
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
              fontSize: tokens.typography.fontSize.xxs,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            Reset
          </button>
        </div>
      )}

      {/* Log entries — minHeight: 0 so flex child can shrink and scroll */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="zendir-log-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "2px 0",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {filteredEntries.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: tokens.colors.text.muted,
              fontSize: 13,
              fontFamily: tokens.typography.fontFamily.primary,
            }}
          >
            {entries.length === 0 ? "No log entries" : "No matching entries"}
          </div>
        ) : usePagination ? (
          <div
            style={{ padding: "2px 0" }}
            role="list"
            aria-label="Log entries"
          >
            {(paginationEntries ?? []).map((entry) => renderRow(entry))}
          </div>
        ) : (
          <div
            style={{
              height: totalHeight,
              position: "relative",
              width: "100%",
            }}
            role="list"
            aria-label="Log entries"
          >
            {visibleRange.start > 0 && (
              <div
                style={{
                  height: visibleRange.start * estimatedRowHeight,
                  width: "100%",
                }}
                aria-hidden
              />
            )}
            {visibleEntries.map((entry) => renderRow(entry))}
            {visibleRange.end < filteredEntries.length && (
              <div
                style={{
                  height:
                    (filteredEntries.length - visibleRange.end) *
                    estimatedRowHeight,
                  width: "100%",
                }}
                aria-hidden
              />
            )}
          </div>
        )}
      </div>

      {/* Pagination footer */}
      {usePagination && filteredEntries.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: tokens.spacing.sm,
            padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            background: tokens.colors.background.surface,
            fontFamily: tokens.typography.fontFamily.primary,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.secondary,
          }}
        >
          <span aria-live="polite">
            Page {currentPage} of {totalPages}
            {filteredEntries.length > 0 && (
              <span style={{ marginLeft: tokens.spacing.sm }}>
                ({((currentPage - 1) * pageSize + 1).toLocaleString()}–
                {Math.min(
                  currentPage * pageSize,
                  filteredEntries.length,
                ).toLocaleString()}{" "}
                of {filteredEntries.length.toLocaleString()})
              </span>
            )}
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setCurrentPage(1);
                setAutoScroll(true);
              }}
              disabled={currentPage <= 1}
              aria-label="First page (newest)"
              style={{
                padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                fontSize: tokens.typography.fontSize.xxs,
                cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                opacity: currentPage <= 1 ? 0.5 : 1,
              }}
            >
              First
            </button>
            <button
              type="button"
              onClick={() => {
                setCurrentPage((p) => Math.max(1, p - 1));
                setAutoScroll(false);
              }}
              disabled={currentPage <= 1}
              aria-label="Previous page"
              style={{
                padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                fontSize: tokens.typography.fontSize.xxs,
                cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                opacity: currentPage <= 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => {
                setCurrentPage((p) => Math.min(totalPages, p + 1));
                setAutoScroll(false);
              }}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
              style={{
                padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                fontSize: tokens.typography.fontSize.xxs,
                cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                opacity: currentPage >= totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => {
                setCurrentPage(totalPages);
                setAutoScroll(false);
              }}
              disabled={currentPage >= totalPages}
              aria-label="Last page (oldest)"
              style={{
                padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                background: tokens.colors.background.base,
                color: tokens.colors.text.primary,
                fontSize: tokens.typography.fontSize.xxs,
                cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                opacity: currentPage >= totalPages ? 0.5 : 1,
              }}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Status bar */}
      <style>{`
        .zendir-log-scroll::-webkit-scrollbar { width: 6px; }
        .zendir-log-scroll::-webkit-scrollbar-track { background: transparent; }
        .zendir-log-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
        .zendir-log-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
        .zendir-log-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.15) transparent; }
      `}</style>

      {paused && (
        <div
          role="status"
          style={{
            padding: `${tokens.spacing.xs} ${tokens.spacing.smd}`,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            background: `${tokens.colors.status.caution}15`,
            color: tokens.colors.status.caution,
            fontSize: tokens.typography.fontSize.sm,
            textAlign: "center",
            fontFamily: tokens.typography.fontFamily.primary,
            fontWeight: tokens.typography.fontWeight.medium,
          }}
        >
          Log paused — new entries are buffered
        </div>
      )}
    </div>
  );
});

export default LogViewer;
